import type React from "react";
import { createContext, useContext, useState, useEffect } from "react";

export interface Roaster {
  roasterName: string;
  roasterLogoUrl: string | null;
}

interface AuthContextType {
  roaster: Roaster;
  isAuthenticated: boolean;
  isOnboarded: boolean;
  connectedPlatform: "shopify" | "woocommerce" | null;
  completeOnboarding: (platform: "shopify" | "woocommerce") => void;
  logout: () => void;
}

const defaultRoaster: Roaster = {
  roasterName: "Demo Roaster",
  roasterLogoUrl: null,
};

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

// Sample data for Storybook stories
export const sampleAuthContext: AuthContextType = {
  roaster: {
    roasterName: "Premium Coffee Roasters",
    roasterLogoUrl: null,
  },
  isAuthenticated: true,
  isOnboarded: true,
  connectedPlatform: "shopify",
  completeOnboarding: () => {},
  logout: () => {},
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [roaster, setRoaster] = useState<Roaster>(defaultRoaster);
  const [isAuthenticated, setIsAuthenticated] = useState(true); // Default to true for demo
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [connectedPlatform, setConnectedPlatform] = useState<
    "shopify" | "woocommerce" | null
  >(null);

  useEffect(() => {
    // Check localStorage for saved auth state
    const savedOnboarded = localStorage.getItem("isOnboarded");
    const savedPlatform = localStorage.getItem("connectedPlatform") as
      | "shopify"
      | "woocommerce"
      | null;

    if (savedOnboarded === "true") {
      setIsOnboarded(true);
    }
    if (savedPlatform) {
      setConnectedPlatform(savedPlatform);
    }

    // In a real app, fetch roaster info from API
    const savedRoasterName = localStorage.getItem("roasterName");
    const savedRoasterLogo = localStorage.getItem("roasterLogoUrl");
    if (savedRoasterName) {
      setRoaster({
        roasterName: savedRoasterName,
        roasterLogoUrl: savedRoasterLogo || null,
      });
    }
  }, []);

  const completeOnboarding = (platform: "shopify" | "woocommerce") => {
    setIsOnboarded(true);
    setConnectedPlatform(platform);
    localStorage.setItem("isOnboarded", "true");
    localStorage.setItem("connectedPlatform", platform);
  };

  const logout = () => {
    setIsAuthenticated(false);
    setIsOnboarded(false);
    setConnectedPlatform(null);
    localStorage.removeItem("isOnboarded");
    localStorage.removeItem("connectedPlatform");
  };

  return (
    <AuthContext.Provider
      value={{
        roaster,
        isAuthenticated,
        isOnboarded,
        connectedPlatform,
        completeOnboarding,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Mock provider for Storybook stories
export interface MockAuthProviderProps {
  children: React.ReactNode;
  roaster?: Roaster;
  isAuthenticated?: boolean;
  isOnboarded?: boolean;
  connectedPlatform?: "shopify" | "woocommerce" | null;
}

export const mockAuthDefaults: Omit<
  AuthContextType,
  "completeOnboarding" | "logout"
> = {
  roaster: {
    roasterName: "Premium Coffee Roasters",
    roasterLogoUrl: null,
  },
  isAuthenticated: true,
  isOnboarded: true,
  connectedPlatform: "shopify",
};

export function MockAuthProvider({
  children,
  roaster = mockAuthDefaults.roaster,
  isAuthenticated = mockAuthDefaults.isAuthenticated,
  isOnboarded = mockAuthDefaults.isOnboarded,
  connectedPlatform = mockAuthDefaults.connectedPlatform,
}: MockAuthProviderProps) {
  return (
    <AuthContext.Provider
      value={{
        roaster,
        isAuthenticated,
        isOnboarded,
        connectedPlatform,
        completeOnboarding: () => {},
        logout: () => {},
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
